import { TextInput } from '@doxyme/ui';
import { CardElement, Elements, useElements, useStripe, } from '@stripe/react-stripe-js';
import classNames from 'classnames';
import moment from 'moment';
import React, { createRef, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import uuid from 'uuid/v4';
import { useAsyncEffect } from 'use-async-effect';
import { ReCAPTCHA } from '../../../../components/recaptcha';
import { routerHelperFactory } from '../../../../global/common/services/route-helper-factory';
import config from '../../../../global/env.config';
import { selectLocationDetails, selectPlans, selectUser, } from '../../../../store/features/system';
import { ProgressButton } from '../../../core/components';
import Price, { getPlanName } from '../../../core/components/price';
import Tax from '../../../core/components/tax';
import { getAngularServiceOrUndefined } from '../../../core/helpers';
import { getStripeInstance } from '../../../core/helpers/stripe-helpers';
import { createAccountAndLogin, createClinicAccountAndLogin, createSubscription, createUserAccount, } from '../../actions';
import { getUserCurrency } from '../../helpers';
import { trackCustomEventGA4 } from '../../../../global/global.tracking';
import './payment-setup.scss';
import { PlanTypes } from '../../../../modules/core';
import { ClinicPaymentSetup } from './payment-setup-clinic';
import stripeElementOptions from './common/stripe-element-options';
import { SignUpStepper } from './common/SignUpStepper';
import { Loader } from './common/Loader';
import ArrowBack from './common/arrow-back.svg';
const STORAGE_NAME_PLAN_INTERVAL = 'typeFromPricingPage';
function getPlanIntervalFromStorage() {
    const planInterval = (sessionStorage.getItem(STORAGE_NAME_PLAN_INTERVAL) ||
        localStorage.getItem(STORAGE_NAME_PLAN_INTERVAL));
    return planInterval === 'yearly' ? 'yearly' : 'monthly';
}
export const PaymentSetup = () => {
    const queryParams = useLocation();
    const [stripeInstance, setStripeInstance] = useState(null);
    // 'professional' account type is not explictly passed in. It is assumed if the accountType is not 'free' or 'clinic'.
    // see https://doxyme.atlassian.net/browse/CS-17394
    const accountType = new URLSearchParams(queryParams.search).get('accountType') ?? PlanTypes.Professional;
    const hasClinicSignUpPath = Boolean(accountType === PlanTypes.Clinic);
    useAsyncEffect(async () => {
        setStripeInstance(await getStripeInstance());
    }, []);
    if (!stripeInstance) {
        return <div>Loading Stripe...</div>;
    }
    return (<Elements stripe={stripeInstance}>
      {hasClinicSignUpPath ? (<ClinicPaymentSetup />) : (<ProPaymentSetup />)}
    </Elements>);
};
const ProPaymentSetup = () => {
    const [stripeError, setStripeError] = useState(null);
    const [isCaptchaChecked, setIsCaptchaChecked] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const [isCardInfoComplete, setIsCardInfoComplete] = useState(false);
    const [isTaxInfoComplete, setIsTaxInfoComplete] = useState(false);
    const [isPayButtonDisabled, setIsPayButtonDisabled] = useState(true);
    const [isHandlingSubscription, setIsHandlingSubscription] = useState(false);
    const [shouldDisplayLoader, setShouldDisplayLoader] = useState(false);
    const [loaderStatus, setLoaderStatus] = useState(null);
    const [currencyCode, setCurrencyCode] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null); // plan name including currency and interval
    const [selectedProduct, setSelectedProduct] = useState(null); // underlying product
    const [basePrice, setBasePrice] = useState(null);
    const [postalCode, setPostalCode] = useState(null);
    const [countryCode, setCountryCode] = useState(null);
    const [countryCodeTemp, setCountryCodeTemp] = useState(null);
    const [seats, setSeats] = useState(2);
    const [couponCode, setCouponCode] = useState('');
    const [planInterval, setPlanInterval] = useState(getPlanIntervalFromStorage());
    const [showBackupZipCode, setShowBackupZipCode] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const { formatMessage } = useIntl();
    const allPlans = useSelector(selectPlans);
    const currentUser = useSelector(selectUser);
    const locationDetails = useSelector(selectLocationDetails);
    const marketingUrl = routerHelperFactory.getBaseUrl();
    const captchaRef = createRef();
    const calendarDate = moment().format('L');
    const stripe = useStripe();
    const elements = useElements();
    const trackingFactory = getAngularServiceOrUndefined('trackingFactory');
    const [totalPrice, setTotalPrice] = useState(0);
    useEffect(() => {
        if (currentUser == null) {
            history.push('/sign-up');
            return;
        }
        // ensure proper currency
        setCurrency({
            locationDetails,
            setCurrencyCode,
            setSelectedPlan,
            planInterval,
            hasClinicSignUpPath: false,
        });
        // ensure proper base price
        if (allPlans.length > 0 && selectedPlan) {
            const plan = allPlans.find((p) => p.id === selectedPlan || p.stripePlan === selectedPlan);
            if (plan) {
                setBasePrice(plan.pricePerSeat / 100);
                setSelectedProduct(plan.product);
            }
        }
    }, [
        selectedPlan,
        allPlans,
        currentUser,
        history,
        locationDetails,
        planInterval,
    ]);
    useEffect(() => {
        if (isCardInfoComplete && isCaptchaChecked && isTaxInfoComplete) {
            setIsPayButtonDisabled(false);
        }
        else {
            setIsPayButtonDisabled(true);
        }
    }, [isCaptchaChecked, isCardInfoComplete, isTaxInfoComplete]);
    useEffect(() => {
        if (!isHandlingSubscription && captchaRef.current) {
            captchaRef.current.reset();
            setTimeout(() => {
                setRecaptchaToken('');
                setIsCaptchaChecked(false);
            }, 1500);
        }
    }, [isHandlingSubscription]);
    const handleSubmit = useCallback(async (ev) => {
        ev.preventDefault();
        if (!recaptchaToken) {
            return null;
        }
        setIsHandlingSubscription(true);
        await paymentSetupHandler({
            elements,
            currencyCode,
            // Briefly, before a user becomes a full user their
            // current user data is set from the signup form values
            signupFormData: currentUser,
            stripe,
            setStripeError,
            setIsHandlingSubscription,
            setShouldDisplayLoader,
            formatMessage,
            dispatch,
            setLoaderStatus,
            trackingFactory,
            postalCode,
            countryCode,
            recaptchaToken,
            planInterval,
            hasClinicSignUpPath: false,
            seats,
            couponCode,
            totalPrice,
        });
    }, [
        elements,
        currentUser,
        currencyCode,
        postalCode,
        countryCode,
        recaptchaToken,
        totalPrice,
    ]);
    const debouncedSetCountyPostalCode = useDebouncedCallback((card) => {
        if (card.country === 'US' && card.address_zip === null) {
            setCountryCodeTemp(card.country);
            setShowBackupZipCode(true);
        }
        else {
            setShowBackupZipCode(false);
            setPostalCode(card.address_zip);
            setCountryCode(card.country);
            setIsCardInfoComplete(true);
        }
    }, 500);
    const onZipCodeBlur = useCallback((e) => {
        const zipCode = e.target.value;
        setPostalCode(zipCode);
        setCountryCode(countryCodeTemp);
        setIsCardInfoComplete(true);
    }, [countryCodeTemp]);
    const handleChange = useCallback(async (event) => {
        if (event.complete) {
            const cardElement = elements.getElement(CardElement);
            const { error, token } = await stripe.createToken(cardElement);
            if (error) {
                setStripeError(error.message);
                return;
            }
            debouncedSetCountyPostalCode(token.card);
        }
        else {
            setPostalCode(null);
            setCountryCode(null);
            setIsCardInfoComplete(false);
        }
        setStripeError(event.error ? event.error.message : null);
    }, [elements, stripe]);
    const redirectToSignup = () => {
        history.push('/sign-up');
    };
    if (shouldDisplayLoader) {
        return (<Loader onBack={redirectToSignup} status={loaderStatus}/>);
    }
    if (currentUser == null) {
        return null;
    }
    return (<div>
      <div className="sign-up__container">
        <section className="sign-up__section content-box box-vertical scrollable">
          <a className="sign-up__back-button" href={`${marketingUrl}/en/pricing/`} target="_self">
            <ArrowBack className="sign-up__icon-arrow-btn"/>
            <span className="sign-up__back-button-text">
              <FormattedMessage id="auth.sign-up.goBack" defaultMessage="Go back to pricing"/>
            </span>
          </a>

          <header className="logo-header">
            <a className="logo-header-link" href={marketingUrl} rel="noreferer noopener">
              <img className="logo-header-value" src="/assets/images/icons/logo-horizontal.png" alt="logo header"/>
            </a>
          </header>

          <div className="content">
            <SignUpStepper steps={['auth.sign-up.yourInfo', 'auth.sign-up.payment']} currentStepIndex={1}/>

            <div className="payment-setup__section-title">
              <FormattedMessage id="auth.payment-setup.paymentAmount" defaultMessage="Payment Amount"/>
            </div>

            {currencyCode && (<div className="payment-setup__payment-amount">
                <Price currency={currencyCode} item={basePrice}/>

                <Tax email={currentUser.email} planName={selectedPlan} postalCode={postalCode} countryCode={countryCode} seats={1} isValid={setIsTaxInfoComplete}/>
              </div>)}

            <div className="payment-setup__payment-description">
              <div>
                <FormattedMessage id="auth.payment-setup.paymentNotice" defaultMessage={`This is a ${planInterval} payment. We'll automatically charge you starting `} values={{ interval: planInterval }}/>
                <b>
                  <FormattedMessage id="auth.payment-setup.paymentNoticeDate" defaultMessage={`today, ${calendarDate}.`} values={{ calendarDate }}/>
                </b>
              </div>
            </div>

            <div className="payment-setup__checkout-form">
              <div className="form-title payment-setup__section-title">
                <FormattedMessage id="auth.payment-setup.cardNumber" defaultMessage="Card number"/>
              </div>

              <form id="payment-form" onSubmit={handleSubmit} noValidate>
                <div className="payment-setup__card-element" data-test-id="payment-setup-card-container">
                  <CardElement id="card-element" options={stripeElementOptions} onChange={handleChange}/>
                </div>

                {showBackupZipCode && (<div style={{ marginTop: '7px' }}>
                    <div className="form-title payment-setup__section-title">
                      <FormattedMessage id="auth.payment-setup.zipcode" defaultMessage="ZIP/Postal code"/>
                    </div>
                    <TextInput style={{ width: '100px' }} type="numeric" onBlur={onZipCodeBlur}/>
                  </div>)}

                {/* Show any error that happens when processing the payment */}
                {stripeError && (<div className="ui-form-helper-text ui-form-helper-text--error" role="alert">
                    {stripeError}
                  </div>)}

                <p id="payment-setup-recaptcha-label" className="payment-setup__recaptcha-label">
                  <FormattedMessage id="auth.sign-in.recaptcha" defaultMessage="Confirm you are not a robot"/>
                </p>
                <div id="g-recaptcha" className="payment-setup__recaptcha-container" data-test-id="payment-setup-recaptcha" role="group" tabIndex={0} aria-labelledby="payment-setup-recaptcha-label">
                  <ReCAPTCHA className="react-recaptcha" ref={captchaRef} sitekey={config.googleReCaptchaV2CheckboxSiteKey} onChange={(token) => {
            setRecaptchaToken(token);
            setIsCaptchaChecked(true);
        }} onExpired={() => {
            setRecaptchaToken('');
            setIsCaptchaChecked(false);
        }}/>
                </div>

                <div className="payment-setup__progress-buttons">
                  <button type="button" className="button button--stroke show-hide-btn payment-setup__go-back-btn" onClick={() => history.push('/sign-up')}>
                    <FormattedMessage id="auth.payment-setup.goBackBottom" defaultMessage="Go back"/>
                  </button>

                  <ProgressButton className={classNames('payment-setup__submit-btn btn btn-primary progress-button space-md-bottom', {
            'payment-setup__progress-button--disabled': isPayButtonDisabled,
        })} type="submit" validation-submit="form" data-test-id="payment-setup-submit" isLoading={isHandlingSubscription} hasError={Boolean(stripeError)} disabled={isPayButtonDisabled}>
                    <span>
                      <FormattedMessage id="auth.payment-setup.submit" defaultMessage="Pay"/>
                    </span>
                  </ProgressButton>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>);
};
export async function setCurrency(currencyProps) {
    const { locationDetails, setCurrencyCode, setSelectedPlan, planInterval, hasClinicSignUpPath, } = currencyProps;
    const storedCurrency = await getUserCurrency(locationDetails);
    if (storedCurrency) {
        const selectedPlanName = getPlanName(`${hasClinicSignUpPath ? 'clinic' : 'pro'}_${planInterval}-v2`, storedCurrency);
        setCurrencyCode(storedCurrency);
        setSelectedPlan(selectedPlanName);
    }
}
export async function paymentSetupHandler(paymentProps) {
    const { elements, currencyCode, signupFormData, stripe, setStripeError, setIsHandlingSubscription, setShouldDisplayLoader, formatMessage, dispatch, setLoaderStatus, trackingFactory, postalCode, countryCode, recaptchaToken, planInterval, hasClinicSignUpPath, seats, couponCode, totalPrice, } = paymentProps;
    try {
        // Should be refactored during migration to V2,
        // ticket -> https://doxyme.atlassian.net/browse/CS-10301
        const cardElement = elements.getElement(CardElement);
        let planId = '';
        planId = hasClinicSignUpPath
            ? `plan-default-${currencyCode.toLowerCase()}-clinic_${planInterval}-v2`
            : `plan-default-${currencyCode.toLowerCase()}-pro_${planInterval}-v2`;
        let idempotencyKey = window.sessionStorage.getItem('idempotency-key.payment-setup');
        if (!idempotencyKey) {
            idempotencyKey = uuid();
            window.sessionStorage.setItem('idempotency-key.payment-setup', idempotencyKey);
        }
        const { subscription, customer } = await createSubscription(signupFormData.email, countryCode, planId, postalCode, idempotencyKey, hasClinicSignUpPath ? seats : null, hasClinicSignUpPath ? couponCode : null, hasClinicSignUpPath ? totalPrice : null);
        // In the event a 100% discount code is used, the Stripe subscription
        // will not have a subscription.latest_invoice.payment_intent, as
        // no payment gets made.
        //
        // It will instead have a pending_setup_intent, which is used to
        // denote an intention to make a payment on the next invoice.
        // The pending_setup_intent will contain the payment method
        //
        // See https://docs.stripe.com/api/subscriptions/object#subscription_object-latest_invoice
        const hasPaymentIntent = subscription?.latest_invoice?.payment_intent;
        const hasPendingSetupIntent = subscription?.pending_setup_intent;
        let paymentMethod = null;
        if (Boolean(hasPendingSetupIntent)) {
            const clientSecret = subscription.pending_setup_intent.client_secret;
            const cardSetup = await stripe.confirmCardSetup(clientSecret, {
                payment_method: {
                    card: cardElement,
                },
            });
            paymentMethod = cardSetup.setupIntent.payment_method;
            if (cardSetup.error) {
                console.warn(`stripe.confirmCardSetup has failed: ${cardSetup.error.message}, ` +
                    'for more details see https://docs.stripe.com/js/setup_intents/confirm_card_setup');
                setStripeError(cardSetup.error.message);
            }
        }
        if (Boolean(hasPaymentIntent) || !Boolean(hasPendingSetupIntent)) {
            const clientSecret = subscription.latest_invoice.payment_intent.client_secret;
            const payment = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                },
                setup_future_usage: 'off_session',
            });
            if (payment.error) {
                // NOTE: This is deliberately allowed to fall through, as our process
                // doesn't currently handle the 3DS callback or may not be required
                // either way this has been left in so that we can record the scale
                // of the problem. We don't have reports of payments failing regularly
                // so I'm unclear whether 3D Secure checks (https://docs.stripe.com/payments/3d-secure#three-ds-radar)
                // are common with our customers
                console.warn(`stripe.confirmCardPayment has failed: ${payment.error.message}, ` +
                    'for more details see https://docs.stripe.com/js/payment_intents/confirm_card_payment');
                setStripeError(payment.error.message);
            }
            paymentMethod = payment.paymentIntent.payment_method;
        }
        // Happy path
        setIsHandlingSubscription(false);
        setTimeout(() => {
            setShouldDisplayLoader(true);
        }, 1000);
        if (signupFormData.title ===
            formatMessage({ id: 'user.titles.Other', defaultMessage: '--' })) {
            signupFormData.title = '';
        }
        const newUserAccount = await createUserAccount(signupFormData, subscription, customer, paymentMethod);
        if (newUserAccount.id) {
            if (!Boolean(hasPaymentIntent)) {
                console.warn('A 100% discount code was used to sign-up', {
                    user_id: newUserAccount.id,
                    coupon_code: couponCode,
                });
            }
            if (hasClinicSignUpPath) {
                trackCustomEventGA4('sign_up_clinic_2', {
                    current_subscription: PlanTypes.Clinic,
                    user_id: newUserAccount.id,
                });
                dispatch(createClinicAccountAndLogin(newUserAccount.accessToken, newUserAccount, newUserAccount.institution));
            }
            else {
                trackCustomEventGA4('sign_up_pro_2', {
                    current_subscription: 'pro',
                    user_id: newUserAccount.id,
                });
                dispatch(createAccountAndLogin(undefined, recaptchaToken, newUserAccount, undefined, PlanTypes.Professional));
            }
            setLoaderStatus('success');
        }
        else {
            trackingFactory.global.trackWithExplicitProperties('Error Occurred', {
                errorType: 'Create User Account Error',
                user_email: signupFormData.email,
                user_type: 'user',
            });
            setLoaderStatus('failed');
        }
    }
    catch (error) {
        console.error('error', error);
        setTimeout(() => {
            setShouldDisplayLoader(true);
            setTimeout(() => {
                setLoaderStatus('failed');
            }, 3000);
        }, 1000);
    }
    finally {
        window.sessionStorage.removeItem('idempotency-key.payment-setup');
        setIsHandlingSubscription(false);
    }
}
